import { Alert, Button, Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useContracts } from './ContractProvider';
import { contractStatusLabel } from './labels';
import { formatMinor } from '../format';
import { errorText } from '../session/errorText';
import type { PAContract } from '../api/money';

/**
 * MoneyPage — the frame every money screen shares: a title, the contract picker, and the
 * four states the contract list can be in.
 *
 * It exists so that «which contract am I looking at» is answered ONCE. Seven screens
 * repeating the load-choose-render dance is seven chances for one of them to choose
 * differently, and a screen that silently reads a different contract from the one its
 * heading names is unfalsifiable from inside itself.
 *
 * The child is a FUNCTION of the chosen contract, so a screen cannot render before there is
 * one — no `contract!`, no null check per screen, and no request built with
 * `contract_id=undefined`, which the server answers 404 for, indistinguishably from «not
 * yours», and which would therefore read as a permissions fault.
 */
export function MoneyPage({
  titleKey,
  leadKey,
  lead,
  children,
}: {
  titleKey: string;
  leadKey?: string;
  /** An already-rendered lead — for the one screen whose lead interpolates a value. */
  lead?: ReactNode;
  children: (contract: PAContract) => ReactNode;
}) {
  const { t } = useTranslation();
  const { status, error, contracts, selected, select, reload } = useContracts();

  return (
    <>
      <h1 className="pa-page-title">{t(titleKey)}</h1>
      {lead ?? (leadKey ? <p className="pa-page-lead">{t(leadKey)}</p> : null)}

      {contracts.length > 1 && selected ? (
        <div className="pa-picker">
          <label className="pa-picker-label" htmlFor="pa-contract">
            {t('money.contractLabel')}
          </label>
          <Select<number>
            id="pa-contract"
            className="pa-picker-select"
            value={selected.id}
            onChange={select}
            options={contracts.map((c) => ({
              value: c.id,
              // The number, the balance AND a terminated contract's badge. The balance is
              // the thing the subscriber is choosing between with several contracts, and
              // reading it only after the switch means switching twice.
              //
              // ⚠ THE BADGE IS THE HALF THAT WAS MISSING. `status` is on the wire and this
              // picker rendered number + balance only, so a closed contract sat in the list
              // looking exactly like a live one — and, with no contract in this database
              // carrying `default_in_personal_area`, it could be the one the portal OPENED
              // on (the fallback is the first row, i.e. the OLDEST contract).
              label: contractOptionLabel(c, t),
            }))}
          />
        </div>
      ) : null}

      {status === 'loading' ? <PageSpinner /> : null}

      {status === 'error' ? (
        <>
          <Alert
            type="error"
            showIcon
            message={t('common.unavailableTitle')}
            description={errorText(error, t)}
            style={{ marginBottom: 16 }}
          />
          <Button onClick={reload}>{t('common.retry')}</Button>
        </>
      ) : null}

      {status === 'empty' ? <Alert type="info" showIcon message={t('money.noContracts')} /> : null}

      {status === 'ready' && selected ? children(selected) : null}
    </>
  );
}

/** One picker option: «Д-2024-001 · 200,00» — plus «· Расторгнут» when it is closed. */
function contractOptionLabel(c: PAContract, t: TFunction): string {
  const status = contractStatusLabel(c.status, t);
  const base = `${c.number} · ${formatMinor(c.balance)}`;
  return status ? `${base} · ${status}` : base;
}

/** The one spinner shape, so a slow read never looks like a blank page. */
export function PageSpinner() {
  const { t } = useTranslation();
  return (
    <div className="pa-spinner">
      <Spin tip={t('common.loading')} size="large">
        <div style={{ width: 1, height: 32 }} />
      </Spin>
    </div>
  );
}

/**
 * QueryFrame renders the three outcomes of ONE money read: the spinner, the failure with
 * «Повторить», or the data.
 *
 * ⚠ THE FAILURE PANEL NEVER INVITES A PASSWORD. A money read that fails is not an
 * authentication problem — a 401 is intercepted by `usePAQuery` and becomes the login screen
 * through the session provider — so everything that reaches here is ours to explain and his
 * to retry. That is the distinction `SessionProvider`'s `unavailable` state exists to keep,
 * and putting a login form in front of a subscriber whose portal is merely down is the
 * failure it was built to prevent.
 */
export function QueryFrame<T>({
  query,
  reload,
  children,
}: {
  query: { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; error: unknown };
  reload: () => void;
  children: (data: T) => ReactNode;
}) {
  const { t } = useTranslation();
  if (query.status === 'loading') return <PageSpinner />;
  if (query.status === 'error') {
    return (
      <>
        <Alert
          type="error"
          showIcon
          message={t('common.unavailableTitle')}
          description={errorText(query.error, t)}
          style={{ marginBottom: 16 }}
        />
        <Button onClick={reload}>{t('common.retry')}</Button>
      </>
    );
  }
  return <>{children(query.data)}</>;
}
